/* OLEA · Producto experiencia */
(() => {
  if (customElements.get('olea-pdp-init')) return;
  customElements.define('olea-pdp-init', class extends HTMLElement {});

  const DESKTOP = window.matchMedia('(min-width: 990px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  const pad = (n) => String(n).padStart(2, '0');

  function formatMoney(cents, format) {
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const fmt = format || '{{amount_with_comma_separator}} €';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const withDelims = (n, precision, thousands = '.', decimal = ',') => {
      if (isNaN(n) || n == null) return '0';
      const fixed = (n / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      return int.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`) + (dec ? decimal + dec : '');
    };
    const match = fmt.match(placeholder);
    if (!match) return withDelims(value, 2);
    let out;
    switch (match[1]) {
      case 'amount': out = withDelims(value, 2, ',', '.'); break;
      case 'amount_no_decimals': out = withDelims(value, 0, ',', '.'); break;
      case 'amount_with_comma_separator': out = withDelims(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': out = withDelims(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': out = withDelims(value, 2, "'", '.'); break;
      default: out = withDelims(value, 2);
    }
    return fmt.replace(placeholder, out);
  }

  class OleaPdp {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.moneyFormat = root.dataset.moneyFormat;
      this.variants = JSON.parse(root.querySelector('[data-olea-variants]')?.textContent || '[]');
      this.form = root.querySelector('[data-olea-form]');
      this.atc = root.querySelector('[data-olea-atc]');
      this.mediaList = root.querySelector('[data-olea-media-list]');
      this.mediaItems = Array.from(root.querySelectorAll('.olea-pdp__media'));

      this.initHeaderOffset();
      this.initGallery();
      this.initAccordions();
      this.initClamp();
      this.initQuantity();
      this.initVariants();
      this.initForm();
      this.initStickyBar();
    }

    /* ── Cabecera: la sección empieza debajo del header (también si es transparente) ── */
    initHeaderOffset() {
      const header = document.querySelector('header-component') || document.querySelector('.header-group header, header');
      if (!header) return;
      const apply = () => {
        const hr = header.getBoundingClientRect();
        this.headerH = Math.round(hr.height);
        this.root.style.setProperty('--olea-header-h', `${this.headerH}px`);
        if (window.scrollY > 10) return;
        this.root.style.setProperty('--olea-header-overlap', '0px');
        const secTop = this.root.getBoundingClientRect().top;
        const overlap = Math.max(0, Math.round(hr.bottom - secTop));
        this.root.style.setProperty('--olea-header-overlap', `${overlap}px`);
      };
      apply();
      window.addEventListener('load', apply);
      window.addEventListener('resize', apply);
    }

    /* ── Galería ─────────────────────────────────────────── */
    initGallery() {
      const total = this.mediaItems.length;
      const counter = this.root.querySelector('[data-olea-counter]');
      const dotsWrap = this.root.querySelector('[data-olea-dots]');
      if (total > 1) {
        if (counter) {
          counter.hidden = false;
          counter.querySelector('[data-olea-counter-total]').textContent = pad(total);
        }
        if (dotsWrap) {
          dotsWrap.innerHTML = this.mediaItems.map((_, i) => `<span class="olea-pdp__dot${i === 0 ? ' is-active' : ''}"></span>`).join('');
        }
      }
      this.dots = dotsWrap ? Array.from(dotsWrap.children) : [];
      this.counterCurrent = this.root.querySelector('[data-olea-counter-current]');
      this.counterBar = this.root.querySelector('[data-olea-counter-bar]');

      // Aparición suave
      if (this.root.dataset.reveal === 'true' && !REDUCED.matches && 'IntersectionObserver' in window) {
        this.root.classList.add('is-reveal-ready');
        const revealObs = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add('is-visible');
              revealObs.unobserve(e.target);
            }
          });
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
        this.mediaItems.forEach((m) => revealObs.observe(m));
      }

      // Foto activa (escritorio: vertical; móvil: horizontal)
      const setActive = (index) => {
        this.activeIndex = index;
        if (this.counterCurrent) this.counterCurrent.textContent = pad(index + 1);
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      };
      setActive(0);

      if ('IntersectionObserver' in window && total > 1) {
        const activeObs = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive(this.mediaItems.indexOf(e.target));
          });
        }, { rootMargin: '-45% 0px -45% 0px' });
        const mobileObs = new IntersectionObserver((entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive(this.mediaItems.indexOf(e.target));
          });
        }, { root: this.mediaList, threshold: 0.6 });
        const bind = () => {
          activeObs.disconnect();
          mobileObs.disconnect();
          this.mediaItems.forEach((m) => (DESKTOP.matches ? activeObs : mobileObs).observe(m));
        };
        bind();
        DESKTOP.addEventListener?.('change', bind);
      }

      // Barra de progreso del contador
      if (this.counterBar) {
        const onScroll = () => {
          if (!DESKTOP.matches) return;
          const rect = this.mediaList.getBoundingClientRect();
          const scrollable = rect.height - window.innerHeight;
          const progress = scrollable > 0 ? Math.min(1, Math.max(0, -rect.top / scrollable)) : 1;
          this.counterBar.style.setProperty('--olea-progress', progress.toFixed(3));
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    scrollToMedia(el) {
      if (!el) return;
      const behavior = REDUCED.matches ? 'auto' : 'smooth';
      if (DESKTOP.matches) {
        const offset = Math.max(parseInt(getComputedStyle(this.root).getPropertyValue('--olea-sticky-top'), 10) || 0, this.headerH || 0);
        const y = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top: y, behavior });
      } else {
        this.mediaList.scrollTo({ left: el.offsetLeft - this.mediaList.offsetLeft, behavior });
      }
      el.classList.remove('is-highlight');
      void el.offsetWidth;
      el.classList.add('is-highlight');
    }

    mediaFromTarget(target) {
      if (!target) return null;
      if (target.startsWith('pos-')) return this.mediaItems[parseInt(target.slice(4), 10) - 1] || null;
      return document.getElementById(target);
    }

    /* ── Desplegables ────────────────────────────────────── */
    initAccordions() {
      const wrap = this.root.querySelector('[data-olea-accordions]');
      if (!wrap) return;
      const exclusive = wrap.dataset.exclusive === 'true';
      const items = Array.from(wrap.querySelectorAll('[data-olea-acc]'));
      items.forEach((acc) => {
        acc.addEventListener('toggle', () => {
          if (!acc.open) return;
          if (exclusive) items.forEach((o) => { if (o !== acc && o.open) o.open = false; });
          if (this._userToggle) {
            const media = this.mediaFromTarget(acc.dataset.mediaTarget);
            if (media) this.scrollToMedia(media);
          }
        });
        acc.querySelector('summary')?.addEventListener('click', () => {
          this._userToggle = true;
          setTimeout(() => { this._userToggle = false; }, 50);
        });
      });
    }

    initClamp() {
      this.root.querySelectorAll('[data-olea-clamp]').forEach((wrap) => {
        const body = wrap.querySelector('[data-olea-clamp-body]');
        const btn = wrap.querySelector('[data-olea-clamp-toggle]');
        if (!body || !btn) return;
        requestAnimationFrame(() => {
          if (body.scrollHeight > body.clientHeight + 4) btn.hidden = false;
        });
        btn.addEventListener('click', () => {
          const open = wrap.classList.toggle('is-open');
          btn.setAttribute('aria-expanded', String(open));
          btn.querySelector('[data-label-closed]').hidden = open;
          btn.querySelector('[data-label-open]').hidden = !open;
        });
      });
    }

    /* ── Cantidad ────────────────────────────────────────── */
    initQuantity() {
      const input = this.root.querySelector('[data-olea-qty-input]');
      if (!input) return;
      const clamp = (v) => Math.max(1, parseInt(v, 10) || 1);
      this.root.querySelector('[data-olea-qty-minus]')?.addEventListener('click', () => { input.value = clamp(+input.value - 1); });
      this.root.querySelector('[data-olea-qty-plus]')?.addEventListener('click', () => { input.value = clamp(+input.value + 1); });
      input.addEventListener('change', () => { input.value = clamp(input.value); });
    }

    /* ── Variantes ───────────────────────────────────────── */
    initVariants() {
      this.optionSets = Array.from(this.root.querySelectorAll('[data-olea-option]'));
      if (!this.optionSets.length) return;
      this.optionSets.forEach((set) => set.addEventListener('change', () => this.onVariantChange()));
      this.markUnavailable();
    }

    selectedOptions() {
      return this.optionSets.map((set) => set.querySelector('input:checked')?.value);
    }

    markUnavailable() {
      const selected = this.selectedOptions();
      this.optionSets.forEach((set, idx) => {
        set.querySelectorAll('input').forEach((input) => {
          const probe = [...selected];
          probe[idx] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === probe[i]));
          input.nextElementSibling?.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }

    onVariantChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === selected[i]));
      this.markUnavailable();
      this.updateVariant(variant);
    }

    updateVariant(variant) {
      const input = this.root.querySelector('[data-olea-variant-input]');
      const atcText = this.root.querySelector('[data-olea-atc-text]');
      const barBtn = this.root.querySelector('[data-olea-bar-btn]');
      const barPrice = this.root.querySelector('[data-olea-bar-price]');
      const priceEl = this.root.querySelector('[data-olea-price]');
      const sizeEl = this.root.querySelector('[data-olea-size]');

      if (!variant) {
        if (this.atc) { this.atc.disabled = true; atcText.textContent = this.atc.dataset.labelSoldout; }
        if (barBtn) barBtn.disabled = true;
        return;
      }

      this.currentVariant = variant;
      if (input) input.value = variant.id;
      const price = formatMoney(variant.price, this.moneyFormat);

      if (priceEl) {
        priceEl.innerHTML = (variant.compare_at_price > variant.price
          ? `<s class="olea-pdp__compare">${formatMoney(variant.compare_at_price, this.moneyFormat)}</s>`
          : '') + `<span>${price}</span>`;
      }
      if (sizeEl) sizeEl.textContent = variant.title;
      if (barPrice) barPrice.textContent = price;

      if (this.atc) {
        this.atc.disabled = !variant.available;
        if (variant.available) {
          atcText.textContent = this.atc.dataset.labelAdd + (this.atc.dataset.priceInButton === 'true' ? ` · ${price}` : '');
        } else {
          atcText.textContent = this.atc.dataset.labelSoldout;
        }
      }
      if (barBtn) barBtn.disabled = !variant.available;

      // URL
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Foto de la variante
      if (variant.featured_media) {
        const media = this.root.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (media) this.scrollToMedia(media);
      }
    }

    /* ── Añadir al carrito ───────────────────────────────── */
    initForm() {
      if (!this.form || !this.atc) return;
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.addToCart();
      });
    }

    async addToCart() {
      const msg = this.root.querySelector('[data-olea-form-msg]');
      const atcText = this.root.querySelector('[data-olea-atc-text]');
      const original = atcText.textContent;
      const afterAdd = this.root.dataset.afterAdd;
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      this.atc.classList.add('is-loading');
      this.atc.setAttribute('aria-busy', 'true');
      if (msg) { msg.textContent = ''; msg.classList.remove('is-error'); }

      const formData = new FormData(this.form);

      // Secciones del carrito del tema (Atelier / Horizon y Dawn)
      const sectionIds = new Set();
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => sectionIds.add(el.dataset.sectionId));
      const dawnDrawer = document.querySelector('cart-drawer');
      const dawnNotification = document.querySelector('cart-notification');
      const dawnTarget = dawnDrawer || dawnNotification;
      if (dawnTarget && typeof dawnTarget.getSectionsToRender === 'function') {
        dawnTarget.getSectionsToRender().forEach((s) => sectionIds.add(s.id));
      }
      if (sectionIds.size) {
        formData.append('sections', Array.from(sectionIds).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const res = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await res.json();

        if (!res.ok || data.status) {
          if (msg) { msg.textContent = data.description || data.message || 'No se ha podido añadir. Inténtalo de nuevo.'; msg.classList.add('is-error'); }
          return;
        }

        if (afterAdd === 'cart') {
          window.location.href = `${root}cart`;
          return;
        }

        const quantity = Number(formData.get('quantity')) || 1;

        // Atelier / Horizon: evento cart:update → actualiza contador, carrito y lo abre
        this.form.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: {},
            sourceId: String(formData.get('id')),
            data: {
              source: 'product-form-component',
              itemCount: quantity,
              productId: this.root.dataset.productId,
              variantId: String(formData.get('id')),
              sections: data.sections,
            },
          },
        }));

        const horizonDrawer = document.querySelector('cart-drawer-component');
        if (horizonDrawer && !horizonDrawer.hasAttribute('auto-open') && typeof horizonDrawer.open === 'function') {
          horizonDrawer.open();
        }

        // Dawn y derivados
        if (dawnTarget && typeof dawnTarget.renderContents === 'function') {
          dawnDrawer?.classList.remove('is-empty');
          dawnTarget.renderContents(data);
        }

        // Evento genérico para apps
        document.dispatchEvent(new CustomEvent('olea:cart:added', { detail: data }));

        atcText.textContent = this.atc.dataset.labelAdded || original;
        this.atc.classList.add('is-added');
        if (!horizonDrawer && !dawnTarget && msg) {
          msg.innerHTML = `${this.atc.dataset.labelAdded} · <a href="${root}cart">Ver carrito</a>`;
        }
        setTimeout(() => {
          atcText.textContent = original;
          this.atc.classList.remove('is-added');
        }, 2200);
      } catch (err) {
        console.error(err);
        if (msg) { msg.textContent = 'No se ha podido añadir. Revisa tu conexión e inténtalo de nuevo.'; msg.classList.add('is-error'); }
      } finally {
        this.atc.classList.remove('is-loading');
        this.atc.removeAttribute('aria-busy');
      }
    }

    /* ── Barra fija (móvil) ──────────────────────────────── */
    initStickyBar() {
      const bar = this.root.querySelector('[data-olea-bar]');
      const btn = bar?.querySelector('[data-olea-bar-btn]');
      if (!bar || !this.atc || !('IntersectionObserver' in window)) return;

      let atcVisible = true;
      let footerVisible = false;
      const update = () => {
        // Se muestra en cuanto el botón "Añadir" sale por arriba y se oculta al llegar al pie de página.
        const show = !DESKTOP.matches && !atcVisible && !footerVisible && this.atc.getBoundingClientRect().top < 0;
        bar.classList.toggle('is-visible', show);
        bar.setAttribute('aria-hidden', String(!show));
        btn.tabIndex = show ? 0 : -1;
      };
      new IntersectionObserver(([e]) => { atcVisible = e.isIntersecting; update(); }).observe(this.atc);
      const footer = document.querySelector('.shopify-section-group-footer-group, footer');
      // El pie cuenta como "visible" solo cuando ocupa ya buena parte de la pantalla
      if (footer) new IntersectionObserver(([e]) => { footerVisible = e.isIntersecting; update(); }, { rootMargin: '0px 0px -70% 0px' }).observe(footer);
      window.addEventListener('scroll', update, { passive: true });

      btn.addEventListener('click', () => {
        if (this.form.requestSubmit) this.form.requestSubmit(this.atc);
        else this.addToCart();
      });
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-olea-pdp]').forEach((el) => {
      if (el.__oleaPdp) return;
      el.__oleaPdp = new OleaPdp(el);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  // Editor de temas
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
